import supabase from '../../services/supabase';

/**
 * Fetch users from the user_profiles table
 */
export async function fetchUsers({ limit = 10, offset = 0 }) {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .range(offset, offset + limit - 1);
    
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('MCP fetchUsers error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Fetch posts, optionally filtered by user ID
 */
export async function fetchPosts({ userId = null, limit = 10 }) {
  try {
    let query = supabase.from('posts').select(`
      *,
      user_profiles!posts_user_id_fkey(user_id, username, profilePicture)
    `);
    
    if (userId) {
      query = query.eq('user_id', userId);
    }
    
    const { data, error } = await query.limit(limit).order('created_at', { ascending: false });
    
    if (error) throw error;
    
    // Fetch post_stats separately
    if (data && data.length > 0) {
      const postIds = data.map(post => post.id);
      const { data: statsData, error: statsError } = await supabase
        .from('post_stats')
        .select('post_id, likes_count, comments_count')
        .in('post_id', postIds);
      
      if (!statsError && statsData) {
        // Attach stats to posts
        const statsMap = {};
        statsData.forEach(stat => {
          statsMap[stat.post_id] = stat;
        });
        
        data.forEach(post => {
          post.likes_count = statsMap[post.id]?.likes_count || 0;
          post.comments_count = statsMap[post.id]?.comments_count || 0;
        });
      }
    }
    
    return { success: true, data };
  } catch (error) {
    console.error('MCP fetchPosts error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Fetch a post by ID
 */
export async function fetchPostById({ postId }) {
  try {
    const { data, error } = await supabase
      .from('posts')
      .select(`
        *,
        user_profiles!posts_user_id_fkey(user_id, username, profilePicture)
      `)
      .eq('id', postId)
      .single();
    
    if (error) throw error;
    
    // Fetch post_stats separately
    const { data: statsData, error: statsError } = await supabase
      .from('post_stats')
      .select('post_id, likes_count, comments_count')
      .eq('post_id', postId)
      .single();
    
    if (!statsError && statsData) {
      data.likes_count = statsData.likes_count || 0;
      data.comments_count = statsData.comments_count || 0;
    }
    
    return { success: true, data };
  } catch (error) {
    console.error('MCP fetchPostById error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Create a new post
 */
export async function createPost({ userId, title, content, country = null, tags = '', image_url = null, post_date = null }) {
  try {
    // Validate inputs
    if (!userId || !title || !content) {
      return { 
        success: false, 
        error: 'Missing required fields (userId, title, content)' 
      };
    }
    
    const { data, error } = await supabase
      .from('posts')
      .insert([{ 
        user_id: userId,
        title,
        content,
        country,
        tags,
        post_date,
        created_at: new Date().toISOString()
      }])
      .select();
    
    if (error) throw error;
    
    // Initialize post stats
    const { error: statsError } = await supabase
      .from('post_stats')
      .insert([{
        post_id: data[0].id,
        likes_count: 0,
        comments_count: 0
      }]);

    if (statsError) {
      console.error('Error creating post stats:', statsError);
      // Don't throw here, the post was created successfully
    }
    
    return { success: true, data };
  } catch (error) {
    console.error('MCP createPost error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Like a post (toggle)
 */
export async function toggleLike({ userId, postId }) {
  try {
    if (!userId || !postId) {
      return { success: false, error: 'Missing required fields (userId, postId)' };
    }
    
    // Check if the like already exists
    const { data: existingLike, error: checkError } = await supabase
      .from('post_likes')
      .select('*')
      .eq('user_id', userId)
      .eq('post_id', postId)
      .maybeSingle();
    
    if (checkError) throw checkError;
    
    let result;
    
    if (existingLike) {
      // Unlike: Remove the existing like
      const { error: deleteError } = await supabase
        .from('post_likes')
        .delete()
        .eq('user_id', userId)
        .eq('post_id', postId);
      
      if (deleteError) throw deleteError;
      result = { liked: false };
    } else {
      // Like: Add a new like
      const { error: insertError } = await supabase
        .from('post_likes')
        .insert([{ user_id: userId, post_id: postId }]);
      
      if (insertError) throw insertError;
      result = { liked: true };
    }
    
    // Update the likes count in the post
    const { data: likeCount, error: countError } = await supabase
      .from('post_likes')
      .select('id', { count: 'exact' })
      .eq('post_id', postId);
    
    if (countError) throw countError;
    
    // Update the likes_count in post_stats
    const { error: updateError } = await supabase
      .from('post_stats')
      .update({ 
        likes_count: likeCount.length,
        updated_at: new Date().toISOString()
      })
      .eq('post_id', postId);
    
    if (updateError) throw updateError;
    
    return { success: true, ...result, likeCount: likeCount.length };
  } catch (error) {
    console.error('MCP toggleLike error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Add a comment to a post
 */
export async function addComment({ userId, postId, content, parentId = null }) {
  try {
    if (!userId || !postId || !content) {
      return { success: false, error: 'Missing required fields (userId, postId, content)' };
    }
    
    // Insert comment
    const { data, error } = await supabase
      .from('post_comments')
      .insert([{
        user_id: userId,
        post_id: postId,
        content,
        parent_id: parentId,
        created_at: new Date().toISOString()
      }])
      .select();
    
    if (error) throw error;
    
    // Update the comments count in the post
    const { data: commentCount, error: countError } = await supabase
      .from('post_comments')
      .select('id', { count: 'exact' })
      .eq('post_id', postId);
    
    if (countError) throw countError;
    
    // Update the comments_count in post_stats
    const { error: updateError } = await supabase
      .from('post_stats')
      .update({ 
        comments_count: commentCount.length,
        updated_at: new Date().toISOString()
      })
      .eq('post_id', postId);
    
    if (updateError) throw updateError;
    
    return { success: true, comment: data[0], commentCount: commentCount.length };
  } catch (error) {
    console.error('MCP addComment error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get comments for a post
 */
export async function getComments({ postId, limit = 20 }) {
  try {
    if (!postId) {
      return { success: false, error: 'Missing required field postId' };
    }
    
    const { data, error } = await supabase
      .from('post_comments')
      .select('*, user_profiles(*)')
      .eq('post_id', postId)
      .order('created_at', { ascending: true })
      .limit(limit);
    
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('MCP getComments error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get user profile by ID
 */
export async function getUserProfile({ userId }) {
  try {
    if (!userId) {
      return { success: false, error: 'Missing required field userId' };
    }
    
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', userId)
      .single();
    
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('MCP getUserProfile error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Follow/unfollow a user
 */
export async function toggleFollow({ followerId, followedId }) {
  try {
    if (!followerId || !followedId) {
      return { success: false, error: 'Missing required fields (followerId, followedId)' };
    }
    
    // Check if already following
    const { data: existingFollow, error: checkError } = await supabase
      .from('followers')
      .select('*')
      .eq('follower_id', followerId)
      .eq('followed_id', followedId)
      .maybeSingle();
    
    if (checkError) throw checkError;
    
    let result;
    
    if (existingFollow) {
      // Unfollow (triggers will automatically update counts)
      const { error: deleteError } = await supabase
        .from('followers')
        .delete()
        .eq('follower_id', followerId)
        .eq('followed_id', followedId);
      
      if (deleteError) throw deleteError;
      
      result = { following: false };
    } else {
      // Follow (triggers will automatically update counts)
      const { error: insertError } = await supabase
        .from('followers')
        .insert([{ follower_id: followerId, followed_id: followedId }]);
      
      if (insertError) throw insertError;
      
      result = { following: true };
    }
    
    return { success: true, ...result };
  } catch (error) {
    console.error('MCP toggleFollow error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Fetch posts from users that the current user follows
 */
export async function fetchFollowedUsersPosts({ userId, limit = 10, offset = 0 }) {
  try {
    if (!userId) {
      return { success: false, error: 'Missing required field userId' };
    }
    
    // First get the list of users that this user follows
    const { data: followedUsers, error: followError } = await supabase
      .from('followers')
      .select('followed_id')
      .eq('follower_id', userId);
    
    if (followError) throw followError;
    
    // If user doesn't follow anyone, return empty array
    if (!followedUsers || followedUsers.length === 0) {
      return { success: true, data: [] };
    }
    
    // Extract just the IDs
    const followedUserIds = followedUsers.map(follow => follow.followed_id);
    
    // Get posts from these users
    const { data: posts, error: postsError } = await supabase
      .from('new_post')
      .select('*, user_profiles(*)')
      .in('user_id', followedUserIds)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);
    
    if (postsError) throw postsError;
    
    return { success: true, data: posts || [] };
  } catch (error) {
    console.error('MCP fetchFollowedUsersPosts error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Fetch trending posts based on likes and comments
 */
export async function fetchTrendingPosts({ limit = 10, timeframe = 'week' }) {
  try {
    // Calculate date for timeframe
    let dateFilter = new Date();
    switch (timeframe) {
      case 'day':
        dateFilter.setDate(dateFilter.getDate() - 1);
        break;
      case 'week':
        dateFilter.setDate(dateFilter.getDate() - 7);
        break;
      case 'month':
        dateFilter.setMonth(dateFilter.getMonth() - 1);
        break;
      default:
        dateFilter.setDate(dateFilter.getDate() - 7); // Default to week
    }
    
    const dateString = dateFilter.toISOString();
    
    // Get posts with user data, ordered by engagement metrics
    const { data, error } = await supabase
      .from('new_post')
      .select('*, user_profiles(*)')
      .gte('created_at', dateString)
      .order('likes_count', { ascending: false })
      .order('comments_count', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('MCP fetchTrendingPosts error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Add indexes to improve database performance
 */
export async function addDatabaseIndexes() {
  try {
    // For users who have pgSQL access, we'd typically do:
    // CREATE INDEX IF NOT EXISTS idx_new_post_user_id ON new_post (user_id);
    // etc.
    
    // But since we're using Supabase client API, we'll need a different approach
    // We can check if RLS policies would allow this operation
    console.log('Adding database indexes is not supported via the client API');
    
    return { 
      success: false, 
      message: 'Database schema changes require direct SQL access via the Supabase dashboard or pgAdmin',
      error: 'Operation not supported via client API'
    };
  } catch (error) {
    console.error('MCP addDatabaseIndexes error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Add notifications table for social features
 */
export async function addNotificationsTable() {
  try {
    console.log('Adding database tables is not supported via the client API');
    
    return { 
      success: false, 
      message: 'Database schema changes require direct SQL access via the Supabase dashboard or pgAdmin',
      error: 'Operation not supported via client API'
    };
  } catch (error) {
    console.error('MCP addNotificationsTable error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Add favorites table for saving favorite posts
 */
export async function addFavoritesTable() {
  try {
    console.log('Adding database tables is not supported via the client API');
    
    return { 
      success: false, 
      message: 'Database schema changes require direct SQL access via the Supabase dashboard or pgAdmin',
      error: 'Operation not supported via client API'
    };
  } catch (error) {
    console.error('MCP addFavoritesTable error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Create a full-text search function for posts
 */
export async function setupFullTextSearch() {
  try {
    console.log('Setting up full-text search is not supported via the client API');
    
    return { 
      success: false, 
      message: 'Database schema changes require direct SQL access via the Supabase dashboard or pgAdmin',
      error: 'Operation not supported via client API'
    };
  } catch (error) {
    console.error('MCP setupFullTextSearch error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Add a post to user's favorites
 * @param {string} userId - ID of the user
 * @param {number} postId - ID of the post to favorite
 * @returns {Promise<Object>} The created favorite record
 */
export const addToFavorites = async ({ userId, postId }) => {
  try {
    if (!userId || !postId) {
      throw new Error('User ID and Post ID are required');
    }

    const { data, error } = await supabase
      .from('favorites')
      .insert([{ user_id: userId, post_id: postId }])
      .select('*')
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding to favorites:', error);
    throw error;
  }
};

/**
 * Remove a post from user's favorites
 * @param {string} userId - ID of the user
 * @param {number} postId - ID of the post to unfavorite
 * @returns {Promise<Object>} Result of the operation
 */
export const removeFromFavorites = async ({ userId, postId }) => {
  try {
    if (!userId || !postId) {
      throw new Error('User ID and Post ID are required');
    }

    const { error } = await supabase
      .from('favorites')
      .delete()
      .match({ user_id: userId, post_id: postId });

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error removing from favorites:', error);
    throw error;
  }
};

/**
 * Get a user's favorite posts
 * @param {string} userId - ID of the user
 * @param {number} limit - Number of favorites to return
 * @param {number} offset - Offset for pagination
 * @returns {Promise<Array>} The user's favorite posts with post details
 */
export const getFavorites = async ({ userId, limit = 10, offset = 0 }) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const { data, error } = await supabase
      .from('favorites')
      .select('*, posts(*)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching favorites:', error);
    throw error;
  }
};

/**
 * Increment a post's view count
 * @param {number} postId - ID of the post
 * @returns {Promise<Object>} The updated post
 */
export const incrementViewCount = async ({ postId }) => {
  try {
    if (!postId) {
      throw new Error('Post ID is required');
    }

    // First get the current view count
    const { data: post, error: fetchError } = await supabase
      .from('new_post')
      .select('views')
      .eq('id', postId)
      .single();

    if (fetchError) throw fetchError;

    // Increment the view count
    const views = (post.views || 0) + 1;
    
    const { data, error } = await supabase
      .from('new_post')
      .update({ views: views })
      .eq('id', postId)
      .select('*')
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error incrementing view count:', error);
    throw error;
  }
};

/**
 * Create a notification for a user
 * @param {string} userId - ID of the user to notify
 * @param {string} type - Type of notification (like, comment, follow)
 * @param {string} fromUserId - ID of the user who triggered the notification
 * @param {number} postId - Optional ID of the related post
 * @param {string} message - Optional custom message
 * @returns {Promise<Object>} The created notification
 */
export const createNotification = async ({ userId, type, fromUserId, postId, message }) => {
  try {
    if (!userId || !type || !fromUserId) {
      throw new Error('User ID, type, and from_user_id are required');
    }

    const notificationData = { 
      user_id: userId, 
      type, 
      from_user_id: fromUserId,
      read: false
    };
    
    if (postId) notificationData.post_id = postId;
    if (message) notificationData.message = message;

    const { data, error } = await supabase
      .from('notifications')
      .insert([notificationData])
      .select('*')
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

/**
 * Get notifications for a user
 * @param {string} userId - ID of the user
 * @param {boolean} unreadOnly - Whether to return only unread notifications
 * @param {number} limit - Number of notifications to return
 * @returns {Promise<Array>} The user's notifications
 */
export const getNotifications = async ({ userId, unreadOnly = false, limit = 20 }) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    let query = supabase
      .from('notifications')
      .select('*, from_user:from_user_id(username, avatar_url), posts(*)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (unreadOnly) {
      query = query.eq('read', false);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

/**
 * Mark a notification as read
 * @param {number} notificationId - ID of the notification
 * @returns {Promise<Object>} The updated notification
 */
export const markNotificationRead = async ({ notificationId }) => {
  try {
    if (!notificationId) {
      throw new Error('Notification ID is required');
    }

    const { data, error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId)
      .select('*')
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read for a user
 * @param {string} userId - ID of the user
 * @returns {Promise<Object>} Result of the operation
 */
export const markAllNotificationsRead = async ({ userId }) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', userId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Search posts by keywords
 * @param {string} query - Search query
 * @param {number} limit - Number of results to return
 * @returns {Promise<Array>} Matching posts
 */
export const searchPosts = async ({ query, limit = 20 }) => {
  try {
    if (!query) {
      throw new Error('Search query is required');
    }

    // Create a simple full-text search using ILIKE
    const { data, error } = await supabase
      .from('new_post')
      .select('*, user_profiles(*)')
      .or(`title.ilike.%${query}%,content.ilike.%${query}%,tags.ilike.%${query}%`)
      .limit(limit);

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error searching posts:', error);
    throw error;
  }
};

/**
 * Toggle a post as favorite/unfavorite
 */
export async function toggleFavorite({ userId, postId }) {
  try {
    if (!userId || !postId) {
      return { success: false, error: 'Missing required fields (userId, postId)' };
    }
    
    // Check if the favorite already exists
    const { data: existingFavorite, error: checkError } = await supabase
      .from('user_favorites')
      .select('*')
      .eq('user_id', userId)
      .eq('post_id', postId)
      .maybeSingle();
    
    if (checkError) throw checkError;
    
    let result;
    
    if (existingFavorite) {
      // Remove from favorites
      const { error: deleteError } = await supabase
        .from('user_favorites')
        .delete()
        .eq('user_id', userId)
        .eq('post_id', postId);
      
      if (deleteError) throw deleteError;
      result = { favorited: false };
    } else {
      // Add to favorites
      const { error: insertError } = await supabase
        .from('user_favorites')
        .insert([{ user_id: userId, post_id: postId, created_at: new Date().toISOString() }]);
      
      if (insertError) throw insertError;
      result = { favorited: true };
    }
    
    return { success: true, ...result };
  } catch (error) {
    console.error('MCP toggleFavorite error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Fetch user's favorited posts
 */
export async function fetchFavoritePosts({ userId, limit = 10, offset = 0 }) {
  try {
    if (!userId) {
      return { success: false, error: 'Missing required field userId' };
    }
    
    // Get favorite post IDs for the user
    const { data: favorites, error: favoritesError } = await supabase
      .from('user_favorites')
      .select('post_id')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);
    
    if (favoritesError) throw favoritesError;
    
    // If user doesn't have any favorites, return empty array
    if (!favorites || favorites.length === 0) {
      return { success: true, data: [] };
    }
    
    // Extract just the post IDs
    const postIds = favorites.map(favorite => favorite.post_id);
    
    // Get full post data including user profiles
    const { data: posts, error: postsError } = await supabase
      .from('new_post')
      .select('*, user_profiles(*)')
      .in('id', postIds);
    
    if (postsError) throw postsError;
    
    return { success: true, data: posts || [] };
  } catch (error) {
    console.error('MCP fetchFavoritePosts error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Update a user's profile information
 */
export async function updateUserProfile({ userId, username, fullName, bio, avatar_url, website, location }) {
  try {
    if (!userId) {
      return { success: false, error: 'Missing required field userId' };
    }

    // Build the update object with only the fields that are provided
    const updates = {};
    if (username !== undefined) updates.username = username;
    if (fullName !== undefined) updates.full_name = fullName;
    if (bio !== undefined) updates.bio = bio;
    if (avatar_url !== undefined) updates.avatar_url = avatar_url;
    if (website !== undefined) updates.website = website;
    if (location !== undefined) updates.location = location;

    // Only proceed if there are fields to update
    if (Object.keys(updates).length === 0) {
      return { success: false, error: 'No fields provided for update' };
    }

    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('user_id', userId)
      .select();

    if (error) throw error;
    return { success: true, data: data[0] };
  } catch (error) {
    console.error('MCP updateUserProfile error:', error);
    return { success: false, error: error.message };
  }
} 